import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@application/config';
import { Observable } from 'rxjs';
import {
  TInactivationPreview,
  TInactivationPreviewPersons,
  TInactivationPreviewPersonsRequest,
  TInactivationRule,
  TInactivationRuleInput,
  TInactivationRuleReview,
} from '../entities/inactivation-rule.model';

/**
 * HTTP of the inactivation rule (personservice `InactivationRuleController`).
 *
 * `GET` is open to the session; every other route needs
 * `RegrasInativacaoAlterar` (306) and answers 403 without it. Bodies carry
 * only the editable fields (`toInactivationRuleInput`, `CrmAuto` included):
 * the backend refuses unknown members (422).
 */
@Injectable({ providedIn: 'root' })
export class InactivationRuleService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.personService;

  /** The version in force. `Version = 0` = never saved (rule off). */
  public getCurrent(): Observable<TInactivationRule> {
    return this.http.get<TInactivationRule>(
      `${this.baseUrl}/InactivationRule`,
      {
        withCredentials: true,
      }
    );
  }

  /** Saves the rule as the next version (201). 409 if someone saved at the same time. */
  public save(rule: TInactivationRuleInput): Observable<TInactivationRule> {
    return this.http.post<TInactivationRule>(
      `${this.baseUrl}/InactivationRule`,
      rule,
      { withCredentials: true }
    );
  }

  /** Totals and groups of an unsaved rule, by the routine's own evaluator. */
  public preview(
    rule: TInactivationRuleInput
  ): Observable<TInactivationPreview> {
    return this.http.post<TInactivationPreview>(
      `${this.baseUrl}/InactivationRule/Preview`,
      rule,
      { withCredentials: true }
    );
  }

  /** One page of people of the preview (`Top` ≤ 1000). */
  public previewPersons(
    request: TInactivationPreviewPersonsRequest
  ): Observable<TInactivationPreviewPersons> {
    return this.http.post<TInactivationPreviewPersons>(
      `${this.baseUrl}/InactivationRule/Preview/Persons`,
      request,
      { withCredentials: true }
    );
  }

  /** The cutover review: pending, the migrated rule and the suggested one. */
  public getReview(): Observable<TInactivationRuleReview> {
    return this.http.get<TInactivationRuleReview>(
      `${this.baseUrl}/InactivationRule/Review`,
      { withCredentials: true }
    );
  }

  /** Confirms the review with the chosen rule (201). 409 once it is done. */
  public confirmReview(
    rule: TInactivationRuleInput
  ): Observable<TInactivationRule> {
    return this.http.post<TInactivationRule>(
      `${this.baseUrl}/InactivationRule/Review`,
      rule,
      { withCredentials: true }
    );
  }
}
